import { CreditCard, StoredBankAccount } from '@zooplus/checkout-state';

import { CardType, Nullable, SavedBankDetailsType } from '@types-internal';

export const supportedPaymentMethods = {
  BILL: 'R',
  DIRECT_DEBIT_SEPA: 'BS',
  IDEAL: 'ID',
  APPLE_PAY: 'AP',
  GOOGLE_PAY: 'GP',
  BANCONTACT: 'BC',
  BANCONTACT_MOBIL: 'BM',
  CASH_ON_DELIVERY: 'CD',
  CREDIT_CARD: 'K',
  DOTPAY: 'DP',
  ONLINE_BANKING_PL: 'OP',
  RIVERTY_INVOICE: 'RI',
  RIVERTY_DIRECT_DEBIT: 'RS',
  MULTIBANCO: 'MB',
  PAYPAL: 'PP',
  PAYTRAIL: 'PT',
  PREPAYMENT: 'VK',
  TRUSTLY: 'TY',
  TWINT: 'TW',
  KLARNA_PAY_LATER: 'KL',
  KLARNA_PAY_OVER_TIME: 'KO',
  BLIK: 'BL',
  BLIK_VIRTUAL: 'BLK',
} as const;

/**
 * Issuer id of BLIK within the Online Banking PL (OP) issuer list.
 * When the standalone BLIK feature is enabled, this issuer is pulled out of OP
 * and surfaced as its own virtual payment method (short name `BLK`).
 */
export const BLIK_ISSUER_ID = '154';

export const flippedSupportedPMs = {
  R: 'BILL',
  BS: 'DIRECT_DEBIT_SEPA',
  ID: 'IDEAL',
  AP: 'APPLE_PAY',
  GP: 'GOOGLE_PAY',
  BC: 'BANCONTACT',
  BM: 'BANCONTACT_MOBILE',
  CD: 'CASH_ON_DELIVERY',
  K: 'CREDIT_CARD',
  DP: 'DOTPAY',
  OP: 'ONLINE_BANKING_PL',
  MB: 'MULTIBANCO',
  PP: 'PAYPAL',
  PT: 'PAYTRAIL',
  VK: 'PREPAYMENT',
  TY: 'TRUSTLY',
  RI: 'RIVERTY_INVOICE',
  RS: 'RIVERTY_DIRECT_DEBIT',
  TW: 'TWINT',
  KL: 'KLARNA_PAY_LATER',
  KO: 'KLARNA_PAY_OVER_TIME',
  BL: 'BLIK',
  BLK: 'BLIK_VIRTUAL',
} as const;

export const bancontactPaymentMethods = ['BC', 'BM'];

export type PaymentMethod = keyof typeof supportedPaymentMethods;
export type PaymentMethodShortName = (typeof supportedPaymentMethods)[PaymentMethod];
export type CreditCardWithPaymentMethod = CreditCard & {
  paymentMethod: Extract<PaymentMethod, 'CREDIT_CARD' | 'BANCONTACT'>;
  paymentMethodShortName: Extract<PaymentMethodShortName, 'K' | 'BC'>;
  isCreditCard: true;
};

export type StoredBankAccountWithPaymentMethod = StoredBankAccount & {
  paymentMethod: Extract<PaymentMethod, 'RIVERTY_DIRECT_DEBIT'>;
  paymentMethodShortName: Extract<PaymentMethodShortName, 'RS'>;
  isCreditCard: false;
};

export type StoredPaymentMethodInfo =
  | CreditCardWithPaymentMethod
  | StoredBankAccountWithPaymentMethod;

export type CreditCardBrand =
  | 'MASTER_CARD'
  | 'VISA'
  | 'DINERS_CLUB'
  | 'AMERICAN_EXPRESS'
  | 'MAESTRO'
  | 'CARTE_BANCAIRE'
  | 'BCMC'
  | 'OTHER';

export type SavedBankDetailsBrand = 'RIVERTY_DIRECT_DEBIT';

export const cardTypeOfCreditCardBrand: Record<
  CreditCardBrand | SavedBankDetailsBrand,
  Nullable<CardType | SavedBankDetailsType>
> = {
  MASTER_CARD: 'mastercard',
  VISA: 'visa',
  DINERS_CLUB: 'diners-club',
  AMERICAN_EXPRESS: 'american-express',
  MAESTRO: 'maestro',
  CARTE_BANCAIRE: 'cartebancaire',
  BCMC: 'bcmc',
  RIVERTY_DIRECT_DEBIT: 'rivertyDirectDebit',
  OTHER: null,
};

export type CardTypeOfCreditCardBrand = typeof cardTypeOfCreditCardBrand;

export const creditCardBrandOfCardType: Record<CardType, CreditCardBrand> = {
  mastercard: 'MASTER_CARD',
  visa: 'VISA',
  'diners-club': 'DINERS_CLUB',
  'american-express': 'AMERICAN_EXPRESS',
  maestro: 'MAESTRO',
  cartebancaire: 'CARTE_BANCAIRE',
  discover: 'OTHER',
  jcb: 'OTHER',
  unionpay: 'OTHER',
  elo: 'OTHER',
  mir: 'OTHER',
  hiper: 'OTHER',
  hipercard: 'OTHER',
  bcmc: 'OTHER',
};
